"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "../utils";
import type { ButtonProps } from "./button";
import { buttonVariants } from "./button";
const Pagination = ({
  className,
  ...props
}: React.ComponentProps<"nav">) => <nav role="navigation" aria-label="pagination" className={cn("mx-auto flex w-full justify-center", className)} {...props} data-sentry-component="Pagination" data-sentry-source-file="pagination.tsx" />;
Pagination.displayName = "Pagination";
const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(({
  className,
  ...props
}, ref) => <ul ref={ref} className={cn("flex flex-row items-center gap-1", className)} {...props} />);
PaginationContent.displayName = "PaginationContent";
const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(({
  className,
  ...props
}, ref) => <li ref={ref} className={cn("", className)} {...props} />);
PaginationItem.displayName = "PaginationItem";
type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
} & Pick<ButtonProps, "size"> & React.ComponentProps<"a">;
const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  disabled = false,
  ...props
}: PaginationLinkProps) => <a aria-current={isActive ? "page" : undefined} role="link" aria-disabled={disabled} className={cn(buttonVariants({
  variant: isActive ? "outline" : "text",
  size
}), {
  "bg-secondary text-secondary-foreground hover:bg-secondary/80": isActive,
  "text-current": !isActive
}, "cursor-pointer", disabled && "pointer-events-none cursor-not-allowed opacity-50", className)} {...props} data-sentry-component="PaginationLink" data-sentry-source-file="pagination.tsx" />;
PaginationLink.displayName = "PaginationLink";
const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => <PaginationLink aria-label="Go to previous page" size="default" className={cn("gap-1 pl-2.5", className)} {...props} data-sentry-element="PaginationLink" data-sentry-component="PaginationPrevious" data-sentry-source-file="pagination.tsx">
    <ChevronLeft className="h-4 w-4" data-sentry-element="ChevronLeft" data-sentry-source-file="pagination.tsx" />
    <span>Previous</span>
  </PaginationLink>;
PaginationPrevious.displayName = "PaginationPrevious";
const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => <PaginationLink aria-label="Go to next page" size="default" className={cn("gap-1 pr-2.5", className)} {...props} data-sentry-element="PaginationLink" data-sentry-component="PaginationNext" data-sentry-source-file="pagination.tsx">
    <span>Next</span>
    <ChevronRight className="h-4 w-4" data-sentry-element="ChevronRight" data-sentry-source-file="pagination.tsx" />
  </PaginationLink>;
PaginationNext.displayName = "PaginationNext";
const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => <span aria-hidden className={cn("flex h-9 w-9 items-center justify-center", className)} {...props} data-sentry-component="PaginationEllipsis" data-sentry-source-file="pagination.tsx">
    <MoreHorizontal className="h-4 w-4" data-sentry-element="MoreHorizontal" data-sentry-source-file="pagination.tsx" />
    <span className="sr-only">More pages</span>
  </span>;
PaginationEllipsis.displayName = "PaginationEllipsis";
export { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious };